import React, { useState } from "react";
import { Stack, Grid, Flex, Text, Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { ReactComponent as NewsIcon } from "../../assets/icons/news.svg";
import { ReactComponent as OpenIcon } from "../../assets/icons/open-external.svg";
import { ReactComponent as SupportIcon } from "../../assets/icons/support.svg";
import { ReactComponent as ZendeskLogo } from "../../assets/tools/zendesk.svg";
import { ReactComponent as Outlook } from "../../assets/tools/outlook.svg";
import { ReactComponent as Miro } from "../../assets/tools/miro.svg";
import { ReactComponent as Sharepoint } from "../../assets/tools/sharepoint.svg";
import CompanyNews from "../../components/Home/CompanyNews/CompanyNews";
import CommunicationTools from "../../components/Home/CommunicationTools/CommunicationTools";
import CustomCard from "../../components/Elements/Card/Card";
import SectionTitle from "../../components/Elements/SectionTitle/SectionTitle";
import CreateQuickLinkModal from "../../components/Home/CreateQuickLinkModal/CreateQuickLinkModal";
import ControlPanelSearch from "../../components/Home/ControlPanelSearch/ControlPanelSearch";
import HelpCenter from "../../components/Home/HelpCenter/HelpCenter";
import KnowledgeBase from "../../components/Home/KnowledgeBase/KnowledgeBase";
import QuickLinks, {
  type QuickLink,
} from "../../components/Home/QuickLinks/QuickLinks";
import "./Home.css";

const Home: React.FC = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [quickLinks, setQuickLinks] = useState([
    {
      icon: <Miro key="miro" />,
      appUrl: "miro://app",
      webUrl: "https://miro.com/app",
    },
    {
      icon: <Outlook key="outlook" />,
      appUrl: "outlook://",
      webUrl: "https://outlook.office.com",
    },
    {
      icon: <Sharepoint key="sharepoint" />,
      appUrl: "",
      webUrl: "https://coreX.sharepoint.com",
    },
  ]);

  const openZendesk = () => {
    const timeout = setTimeout(() => {
      window.open("https://coreX.zendesk.com", "_blank");
    }, 2000);
    try {
      window.location.href = "zendesk://";

      window.addEventListener("focus", () => {
        clearTimeout(timeout);
      });
    } catch (error) {
      console.error("Error opening app: ", error);
      window.open("https://coreX.zendesk.com", "_blank");
    }
  };

  const openQuickLink = (link: QuickLink) => {
    if (!link.appUrl) {
      window.open(link.webUrl, "_blank");
      return;
    }
    let appOpened = false;
    const timeout = setTimeout(() => {
      if (!appOpened) {
        window.open(link.webUrl, "_blank");
      }
    }, 2000);

    try {
      window.location.href = link.appUrl;
      window.addEventListener("blur", () => {
        appOpened = true;
        clearTimeout(timeout);
      });
      window.addEventListener("focus", () => {
        clearTimeout(timeout);
      });
    } catch (error) {
      console.error("Error opening app: ", error);
      clearTimeout(timeout);
      window.open(link.webUrl, "_blank");
    }
  };

  const addQuickLink = (
    newLink: { webUrl: string; favicon: string } | null
  ) => {
    if (newLink) {
      setQuickLinks((prevLinks) => [
        ...prevLinks,
        {
          icon: <img src={newLink.favicon} alt="icon" className="h-6 w-6" />,
          appUrl: "",
          webUrl: newLink.webUrl,
        },
      ]);
    }
    close();
  };

  const goToLink = (url: string) => {
    window.open(url, "_blank");
  };
  const SCHEDULE_LINK = 'https://corexperience.sharepoint.com/sites/CoreX/Shared%20Documents/Forms/AllItems.aspx?FolderCTID=0x012000D0E868906E8022499C7066AC5CE5BA2C&isAscending=false&id=%2Fsites%2FCoreX%2FShared%20Documents%2FWorkforce%20Management%20%2D%20L1%2FOperations%20%2D%20L2&view=7&q=schedule&sortField=Modified&viewid=ef1647fe%2D9a44%2D45af%2D9634%2D643a2cfe0eb3'
  return (
    <Flex className="home">
      <Group>
        <Grid className="!p-6 !w-full !max-w-[110rem]">
          <Grid.Col span={{ base: 12, md: 3}} order={{ base: 2, md: 1}}>
            <Flex className="flex flex-col gap-12 justify-center w-full">
              <CompanyNews />
              <Stack className="w-full" gap="0.5rem">
                <SectionTitle Icon={NewsIcon} title="Your schedule" />
                <CustomCard
                  className="!flex-row card-hover"
                  onClick={() => goToLink(SCHEDULE_LINK)}
                >
                  <Text className="inline">Your Work Schedule</Text>
                  <OpenIcon />
                </CustomCard>
              </Stack>
            </Flex>
          </Grid.Col>
          <Grid.Col span={{base: 12, md: 6}} order={{ base: 1, md: 2}}>
            <Flex className="flex flex-col gap-12 justify-center w-full">
              <ControlPanelSearch />
              <Stack className="w-full" gap="0.5rem">
                <SectionTitle
                  Icon={SupportIcon}
                  title="Your Customer Support channels"
                />
                <CustomCard className="card-hover" onClick={openZendesk}>
                  <div className="w-full h-full min-w-[640px] min-h-[189px] bg-neutral-50 rounded-xl cursor-pointer flex justify-center items-center md:min-w-0">
                    <ZendeskLogo />
                  </div>
                </CustomCard>
              </Stack>
              <CommunicationTools />
            </Flex>
          </Grid.Col>
          <Grid.Col span={{base: 12, md:3}} order={3}>
            <Flex
              className="flex content-center w-full gap-12"
              direction="column"
            >
              <HelpCenter />
              <KnowledgeBase />
            </Flex>
          </Grid.Col>
        </Grid>
      </Group>
      <Group className="border-t border-[#2f2f31] w-full !justify-center">
        <QuickLinks
          quickLinks={quickLinks}
          openQuickLink={openQuickLink}
          open={open}
        />
      </Group>
      <CreateQuickLinkModal
        opened={opened}
        onClose={addQuickLink}
      ></CreateQuickLinkModal>
    </Flex>
  );
};

export default Home;
